/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import DragBase from '../_internal/dragbase';
import { DragNodeDataset, DragNodeConnector, DragNodeLabels, DragNodeGroup } from '../../dataset/dragnode';
import { getValidValue, pluck, pluckNumber, ZEROSTRING, extend2 } from '@fusioncharts/core/src/lib';
import { getDepsByType } from '@fusioncharts/core/src/dependency-manager';
import datasetFactory from '../../factories/dragnode-dataset';
import { submitData } from '../_internal/editable-charts';
import axisFactory from '@fusioncharts/charts/src/factories/xy-axis';
import { _mouseEvtHandler } from '@fusioncharts/core/src/base-chart';

const DRAGNODE = 'dragnode';
let configurer, UNDEF;
/**
 * API class of dragcolumn inheriting from column class
 */
class DragNode extends DragBase {
  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'DragNode';
  }

  /**
   * Constructor function of DragNode class
   */
  constructor () {
    super();
    var chart = this;
    chart.fireGroupEvent = true;
    chart.usesXYinCategory = true;
    chart.dontShowLegendByDefault = true;
    this.registerFactory('dataset', datasetFactory, ['vCanvas']);
    this.registerFactory('axis', axisFactory, ['canvas']);
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'DragNode';
  }
  /**
   * function to check if the chart specific data is proper is not
   * this fn is define for specific chart types
   * @return {boolean} if JSON data is valid or not
   */
  _checkInvalidSpecificData () {
    let datasetsJSON = this.getFromEnv('dataSource').dataset; // eslint-disable-line good-practices/no-single-usage-variable
    if (!datasetsJSON) {
      return true;
    }
  }

  /**
   * Decides event type and calls handler manager accordingly.
   *
   * @param {any} e e
   * @param {any} data data
   * @memberof DragNode
   */
  _mouseEvtHandler (e, data) {
    _mouseEvtHandler(this, e, data);
  }
  /**
   * Function to parse the common configurations at dataset level
   * @param {Object} dataObj contains json data of the chart
   */
  parseChartAttr (dataObj) {
    var iapi = this,
      chartAttr = iapi.getFromEnv('dataSource').chart,
      config,
      transcoders = getDepsByType('transcoder');
    super.parseChartAttr.call(this, dataObj);
    config = iapi.config;
    config.formAction = getValidValue(chartAttr.formaction);
    config.showLegend = pluckNumber(chartAttr.showlegend, 0);

    if (chartAttr.submitdataasxml === ZEROSTRING && !chartAttr.formdataformat) {
      chartAttr.formdataformat = transcoders.csv().format;
    }

    config.formDataFormat = pluck(chartAttr.formdataformat,
      transcoders.xml().format);
    config.formTarget = pluck(chartAttr.formtarget, '_self');
    config.formMethod = pluck(chartAttr.formmethod, 'POST');
    config.submitFormAsAjax = pluckNumber(chartAttr.submitformusingajax, 1);
    config.viewMode = pluckNumber(chartAttr.viewmode, 0);
    config.drawTrendRegion = 0;
  }

  /**
   * Parsing the default configuration of the chart
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    var chartConf = this.config;
    chartConf.hasLegend = true;
    chartConf.defaultDatasetType = DRAGNODE;
    chartConf.limitUpdaterEnabled = false;
    chartConf.skipClipping = true;
    chartConf.numVDivLines = 0;
    chartConf.numDivLines = 0;
    chartConf.setadaptivexmin = 1;
    chartConf.showLimits = 0;
    chartConf.showdivlinevalues = 0;
    chartConf.showzeroplane = 0;
    chartConf.showyaxisvalues = 0;
    chartConf.enablemousetracking = true;
    chartConf.showzeroplaneontop = 0;
  }

  /**
   * Function to add the menu options for the menu list
   */
  addConfigureOptions () {
    var chart = this,
      chartConf = chart.config,
      restore,
      submit,
      toolbar = chart.getFromEnv('toolbar'),
      hamburgerMenu = toolbar.getChild(`hamburgerMenu-${toolbar.getId()}-${chart.getId()}-0`), // eslint-disable-line good-practices/no-single-usage-variable
      configureTools = [
        {
          name: 'Add Node',
          handler: function () {
            let manager = chart.getChildren('canvas')[0].getChildren('vCanvas')[0] // eslint-disable-line good-practices/no-single-usage-variable
              .getChildren('datasetGroup_dragNode')[0];
            manager.showNodeAddUI();
          },
          action: 'click'
        },
        {
          name: 'Add Connector',
          handler: function () {
            let manager = chart.getChildren('canvas')[0].getChildren('vCanvas')[0] // eslint-disable-line good-practices/no-single-usage-variable
              .getChildren('datasetGroup_dragNode')[0];
            manager.showConnectorAddUI();
          },
          action: 'click'
        },
        {
          name: 'Add Label',
          handler: function () {
            let manager = chart.getChildren('canvas')[0].getChildren('vCanvas')[0] // eslint-disable-line good-practices/no-single-usage-variable
              .getChildren('datasetGroup_dragNode')[0];
            manager.showLabelUpdateUI();
          },
          action: 'click'
        }
      ];

    if (chartConf.showRestoreBtn) {
      restore = {
        name: chartConf.restoreBtnTitle,
        handler: function () {
          let manager = chart.getChildren('canvas')[0].getChildren('vCanvas')[0] // eslint-disable-line good-practices/no-single-usage-variable
            .getChildren('datasetGroup_dragNode')[0];
          manager.restoreData();
        },
        action: 'click'
      };
      configureTools.push(restore);
    }

    if (chartConf.showFormBtn) {
      submit = {
        name: chartConf.submitBtnTitle,
        handler: function () {
          submitData.call(chart);
        },
        action: 'click'
      };
      configureTools.push(submit);
    }

    // setChartTools(configureTools);
    if (!chart.config.viewMode) {
      hamburgerMenu.appendInMenu(configureTools);
    }
  }

  /**
   * Function to map the dataset class to its type
   * @param {string} dsType the type of the dataset.
   * @return {Class} The corresponding dataset class.
   */
  getDSdef (dsType) {
    switch (dsType) {
      case 'connector' :
        return DragNodeConnector;
      case 'dragnode' :
        return DragNodeDataset;
      case 'dragableLabels' :
        return DragNodeLabels;
    }
  }

  /**
   * Function to determine the group manager associated with the current chartAPI
   * @return {Class} The group class
   */
  getDSGroupdef () {
    return DragNodeGroup;
  }

  /**
   * Fucntion to set the categories of X axes.
   */
  _setCategories () {
    var iapi = this,
      dataObj = iapi.getFromEnv('dataSource'),
      xAxis = iapi.getChildren('xAxis'),
      categories = (dataObj.categories && dataObj.categories[0].category) || [],
      catLen = categories.length, // eslint-disable-line good-practices/no-single-usage-variable
      catArr = [],
      i,
      catObj;

    for (i = 0; i < catLen; i++) {
      catObj = categories[i] || {};
      if (catObj.x !== UNDEF) {
        catArr.push(catObj);
      }
    }

    xAxis && xAxis[0].setTickValues(catArr);
  }

  /**
   * Function to get the jsonData
   * @return {Object} the jsonData
   */
  getJSONData () {
    var chart = this,
      canvas = chart.getChildren('canvas')[0], // eslint-disable-line good-practices/no-single-usage-variable
      vCanvas = canvas.getChildren('vCanvas')[0], // eslint-disable-line good-practices/no-single-usage-variable
      groupManager = vCanvas.getChildren('datasetGroup_dragNode')[0],
      datasetsArr = {},
      jsonObj;

    if (groupManager) {
      datasetsArr = groupManager.getJSONData();
    }

    jsonObj = extend2({}, chart.getFromEnv('dataSource'));
    jsonObj.dataset = datasetsArr.dataset;
    jsonObj.connectors = datasetsArr.connectors;
    jsonObj.labels = datasetsArr.labels;
    return jsonObj;
  }
}
configurer = DragNode.prototype.configure;
export default DragNode;
export { configurer };
